import type { UserSummary } from '@/api/shares'

export interface User {
  id: number
  name: string
  email: string
  phone?: string
  website?: string
}

export interface ApiError {
  message: string
  code?: string
  status?: number
}

export type PlanStatus =
  | 'Draft'
  | 'In Progress'
  | 'Submitted'
  | 'Approved'
  | 'Rejected'
  | 'Locked'
  | 'Sent Back'

// API response types
export type ApiPlanType = 'CORE' | 'SEASONAL'
export type ApiPlanStatus =
  | 'DRAFT'
  | 'IN_PROGRESS'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'REJECTED'
  | 'SENT_BACK'

// Hierarchy summary for plan list API response
export interface HierarchyLineageItem {
  name: string
  level: 'DIVISION' | 'DEPARTMENT' | 'CLASS' | 'SUBCLASS'
}

export interface ApiHierarchySummary {
  id: string
  code: string
  name: string
  level: 'DIVISION' | 'DEPARTMENT' | 'CLASS' | 'SUBCLASS'
  lineage?: HierarchyLineageItem[]
}

export interface ApiPlanSummary {
  id: string
  name: string
  plan_type: ApiPlanType
  status?: ApiPlanStatus
  hierarchy?: ApiHierarchySummary
  owner?: {
    id: string
    email?: string | null
    display_name?: string | null
  }
  created_at: string
  modified_at: string
  versions_count?: number
  active_version_id?: string | null
  active_version_alias?: string | null
  can_access?: boolean
  deleted_at?: string | null
  deleted_by?: {
    id: string
    email?: string | null
    display_name?: string | null
  } | null
}

export interface PaginationMeta {
  skip: number
  limit: number
  total: number
  has_more: boolean
}

export interface PaginatedPlansResponse {
  data: ApiPlanSummary[]
  pagination: PaginationMeta
}

export interface AssortmentPlan {
  id: string
  name: string
  hierarchy: string
  hierarchy_id?: string | null
  versions_count: number
  owner: string
  owner_id: string | null
  status: PlanStatus
  active_version_id?: string | null
  active_version_alias: string
  can_access: boolean
}

export interface AssortmentPlansFilters {
  search?: string
  status?: string
  owner_id?: string
  hierarchy_id?: string
  season_id?: string
  page?: number
  pageSize?: number
}

export interface AssortmentPlansResponse {
  plans: AssortmentPlan[]
  totalCount: number
  totalPages: number
  currentPage: number
}

// Plan Detail types
export type ItemStatus = 'IN_PROGRESS' | 'SUBMITTED' | 'APPROVED' | 'REJECTED'

export interface PlanDetailItem {
  id: string
  skuIdentifier: string // Read-only, derived from hierarchy fields
  skuNumber: string
  description: string
  status: ItemStatus
  isNewSku?: boolean // True if this is a new SKU not in the catalog
  // Hierarchy fields
  division: string | null
  department: string | null
  class_: string | null // 'class' is reserved in JS
  subclass: string | null
  // Vendor
  vendorName: string | null
  // Financial fields
  targetRetail: string | null
  targetCost: string | null
  targetMargin: string | null
  landedCost: number | null
  // Plan units
  tyPlanUnits: number | null
  // Last year data
  lySalesUnits: number | null
  lySellThrough: string | null
  // Store model
  storeModelId: string | null
  activeStoreGroupIds: string[]
  // Variant count (placeholder - hardcoded to 1)
  variantCount: number
}

export interface PlanDetailData {
  id: string
  name: string
  status: ApiPlanStatus
  hierarchy: string
}

export interface PlanDetailResponse extends PlanDetailData {
  items: PlanDetailItem[]
  pagination: {
    page: number
    pageSize: number
    totalCount: number
    totalPages: number
  }
}

// Mutation payloads
export interface UpdatePlanItemPayload {
  skuNumber?: string
  description?: string
  status?: ItemStatus
}

export interface PlanItemActionResponse {
  success: boolean
  item?: PlanDetailItem
}

// Bulk operation types
export interface BulkSubmitRequest {
  line_ids: string[]
}

export interface BulkOperationResult {
  id: string
  success: boolean
  error: string | null
}

export interface BulkOperationResponse {
  succeeded: number
  failed: number
  results: BulkOperationResult[]
}

export interface BulkUpdateStoreGroupsRequest {
  line_ids: string[]
  store_group_ids: string[]
}

// Hierarchy types for plan detail API
export interface HierarchyLineage {
  name: string
  level: 'DIVISION' | 'DEPARTMENT' | 'CLASS' | 'SUBCLASS'
}

export interface ApiHierarchy {
  id: string
  code: string
  name: string
  level: 'DIVISION' | 'DEPARTMENT' | 'CLASS' | 'SUBCLASS'
  lineage: HierarchyLineage[]
}

// Season types
export interface ApiSeason {
  id: string
  name: string
}

// User types for plan detail
export interface ApiUser {
  id: string
  email: string | null
  display_name: string | null
}

// Version types
export interface ApiVersion {
  id: string
  version_number: number
  alias: string | null
  status: ApiPlanStatus
  is_active: boolean
  line_count: number
  created_at: string
}

// Full plan detail response from /plans/{plan_id}
export interface ApiPlanDetailResponse {
  id: string
  name: string
  status: ApiPlanStatus
  row_version: number
  plan_type: ApiPlanType
  description: string | null
  season_id: string | null
  season: ApiSeason | null
  fiscal_year: number | null
  hierarchy_id: string
  hierarchy: ApiHierarchy
  owner: ApiUser
  versions_count: number
  active_version_id: string | null
  created_at: string
  modified_at: string
  versions: ApiVersion[]
  editors: ApiUser[]
  set_reset_date: string | null
  exit_date: string | null
  planned_st_pct: string | null
  end_of_season_st_pct: string | null
  discount_rate: string | null
  target_wos: string | null
  new_store_default_pct: string | null
  sales_plan_per_unit: string | null
  margin_plan_per_unit: string | null
  strategic_role: string | null
  strategic_intent: string | null
  pog_groups_involved: string | null
  pogs_involved: number | null
  feature_locations: string | null
  primary_attribute_ids?: string[]
  primary_attributes?: string[]
  notes: string | null
  created_by: string
  updated_by: string
  updated_at: string
  deleted_at?: string | null
  deleted_by?: {
    id: string
    email?: string | null
    display_name?: string | null
  } | null
}

// Audit Trail types
export type AuditAction =
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'RESTORE'
  | 'STATUS_CHANGE'
  | 'BULK_UPDATE'
  | 'IMPORT'

export type AuditEntityType =
  | 'PLAN'
  | 'PLAN_VERSION'
  | 'LINE_ITEM'
  | 'QUOTE_REQUEST'
  | 'COMMITMENT_LETTER'

export interface FieldChange {
  field: string
  old_value: string | null
  new_value: string | null
}

export interface ActorSummary {
  id: string | null
  display_name: string | null
}

export interface ChangeLogEntry {
  id: string
  entity_type: AuditEntityType
  entity_id: string
  entity_name?: string | null
  entity_display_name?: string | null
  action: AuditAction
  changes: FieldChange[]
  actor: ActorSummary
  timestamp: string
}

export interface ChangeLogResponse {
  data: ChangeLogEntry[]
  pagination: PaginationMeta
}

// Store Model types
export type ModelType = 'STATIC' | 'DYNAMIC'
export type ModelStatus = 'ACTIVE' | 'PROCESSING' | 'ERROR' | 'PENDING'
export type AllocationBasis = 'UNITS' | 'SALES'

export interface StoreModelRead {
  id: string
  name: string
  model_type: ModelType
  status: ModelStatus
  description: string | null
  allocation_basis: AllocationBasis
  store_count: number
  group_count: number
  total_allocation_percent: number
  creator: UserSummary | null
  created_by: string
  updated_by: string
  created_at: string
  updated_at: string
}

export interface StoreModelMemberRead {
  id: string
  store_id: string
  store_name: string
  store_number: string
  region: string | null
  address: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  allocation_percent: number | null
  allocation_override: boolean
  original_allocation_percent: number | null
  total_sales: number | null
  total_units: number | null
  gross_margin_percent: number | null
  sales_override: boolean
  units_override: boolean
  original_total_sales: number | null
  original_total_units: number | null
  store_group_id: string | null
  store_group_name: string | null
  added_at: string
}

export interface AllocationOverride {
  store_id: string
  total_sales?: number | null
  total_units?: number | null
}

export interface AllocationOverrideRequest {
  overrides: AllocationOverride[]
}

export interface AllocationOverrideResponse {
  updated_count: number
  recalculated_count: number
  total_allocation_percent: number
}

export type GroupingMethod =
  | 'MANUAL'
  | 'K_MEANS'
  | 'PERCENTILE'
  | 'CUMULATIVE_CONTRIBUTION'
  | 'ATTRIBUTE'
  | 'POG_VERSION'

export interface StoreGroupRead {
  id: string
  name: string
  description: string | null
  grouping_method: GroupingMethod
  store_model_id: string
  row_version: number
  created_by: string
  updated_by: string
  created_at: string
  updated_at: string
  member_count: number
}

export interface PaginatedStoreGroupsResponse {
  data: StoreGroupRead[]
  pagination: PaginationMeta
}

// Store Group Detail types
export interface StoreGroupMetrics {
  total_sales: number | null
  avg_sales_per_store: number | null
  store_count: number
}

export interface StoreGroupMemberRead {
  id: string
  store_id: string
  store_name: string
  store_number: string
  region: string | null
  address: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  allocation_percent: number | null
  added_at: string
}

export interface StoreGroupDetailRead {
  id: string
  name: string
  description: string | null
  grouping_method: GroupingMethod
  filter_rules: string | null
  store_model_id: string
  row_version: number
  created_by: string
  updated_by: string
  created_at: string
  updated_at: string
  member_count: number
  sample_stores: StoreGroupMemberRead[]
  metrics: StoreGroupMetrics
}

export interface StoreGroupMemberSummary {
  total: number
  by_region: Record<string, number>
}

export interface StoreGroupMemberListResponse {
  data: StoreGroupMemberRead[]
  pagination: PaginationMeta
  summary?: StoreGroupMemberSummary
}

export interface StoreGroupUpdate {
  name: string
}

export interface ApplyGroupingRequest {
  method: GroupingMethod
  config?: Record<string, unknown>
}

export interface StoreModelDetailRead {
  id: string
  name: string
  model_type: ModelType
  status: ModelStatus
  description: string | null
  criteria: StrongModelCriteria | null
  grouping_method: GroupingMethod | null
  grouping_config: Record<string, unknown> | null
  allocation_basis: AllocationBasis
  store_count: number
  group_count: number
  total_allocation_percent: number
  creator: UserSummary | null
  last_recalculated_at: string | null
  next_recalculation_at: string | null
  recalculation_schedule: string | null
  line_item_count: number
  plan_count: number
  use_all_stores: boolean
  processing_id: string | null
  events_url: string | null
  created_by: string
  updated_by: string
  created_at: string
  updated_at: string
}

export interface PaginatedStoreModelsResponse {
  data: StoreModelRead[]
  pagination: PaginationMeta
}

export interface PaginatedStoreModelMembersResponse {
  data: StoreModelMemberRead[]
  pagination: PaginationMeta
}

// Processing types
export type ProcessingStatusType =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'

export interface ProcessingResponse {
  processing_id: string
  resource_id: string
  status: ProcessingStatusType
  events_url: string
}

export interface ProcessingJobRead {
  id: string
  resource_type: string
  resource_id: string
  operation: string
  status: ProcessingStatusType
  progress_percent: number
  current_stage: string | null
  error_code: string | null
  error_message: string | null
  started_at: string | null
  completed_at: string | null
  created_by: string
  created_at: string
  duration_seconds: number | null
}

// Strongly-typed criteria
export type PeriodType = 'LY' | 'LLY' | 'CUSTOM' | 'ROLLING'
export type AggregationMethod = 'TOTAL' | 'AVG_WEEKLY'
export type FilterOperator =
  | 'eq'
  | 'neq'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'like'
  | 'in'
  | 'starts_with'
  | 'contains'
export type BooleanOp = 'AND' | 'OR'

export interface TimeCriteria {
  period_type: PeriodType
  start_date?: string | null
  end_date?: string | null
  rolling_weeks?: number | null
  aggregation_method: AggregationMethod
}

export interface FilterCondition {
  field: string
  operator: FilterOperator
  value: string | number | boolean | string[]
}

export interface FilterGroup {
  op: BooleanOp
  conditions: (FilterCondition | FilterGroup)[]
}

export function isFilterGroup(
  item: FilterCondition | FilterGroup
): item is FilterGroup {
  return 'op' in item && 'conditions' in item && !('field' in item)
}

export interface StrongModelCriteria {
  time: TimeCriteria
  product_expression: FilterGroup | null
  location_expression: FilterGroup | null
  pog_groups: string[] | null
}

export interface StoreModelCreate {
  name: string
  model_type: ModelType
  criteria: StrongModelCriteria
  description?: string | null
  allocation_basis?: AllocationBasis
  use_all_stores?: boolean
  recalculation_schedule?: string | null
}

export interface StoreModelUpdate {
  name?: string | null
  description?: string | null
  criteria?: StrongModelCriteria | null
  allocation_basis?: AllocationBasis | null
  use_all_stores?: boolean | null
  recalculation_schedule?: string | null
}

// Store Attribute types
export interface StoreAttributeInfo {
  name: string
  label: string
  type: 'categorical' | 'numeric'
  groupable: boolean
}

export interface StoreAttributeListResponse {
  attributes: StoreAttributeInfo[]
}

export interface StoreAttributeValuesResponse {
  field: string
  values: string[]
}

// Store types
export interface StoreRead {
  id: string
  external_id: string
  name: string
  store_number: string
  region: string | null
  is_active: boolean
  address: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  country: string | null
  district: string | null
  market: string | null
  store_type: string | null
  square_footage: number | null
  proto_id: string | null
  cluster_id: string | null
  pog_block: string | null
  distribution_center_id: string | null
  synced_at: string | null
  created_at: string
}

export interface PaginatedStoresResponse {
  data: StoreRead[]
  pagination: PaginationMeta
}

// Export types
export type ExportStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'

export interface ExportRead {
  id: string
  resource_type: string
  status: ExportStatus
  processing_id: string | null
  row_count: number | null
  error_message: string | null
  download_url: string | null
  download_url_expires_at: string | null
  created_at: string
  completed_at: string | null
}

// ─── Calendar Types ───────────────────────────────────────────────

export type MilestoneType = 'FREEFORM' | 'STANDARDIZED'
export type DateMode = 'SPECIFIC' | 'CALCULATED'
export type CalcDirection = 'ADD' | 'SUBTRACT'
export type CalcUnit = 'DAY' | 'WEEK' | 'MONTH'

export interface CalendarOwner {
  id: string
  email: string | null
  display_name: string | null
}

export interface CalendarHeaderRead {
  id: string
  name: string
  description: string | null
  owner: CalendarOwner | null
  is_active: boolean
  event_count?: number
  milestone_count?: number
  plan_count?: number
  created_at: string
  updated_at: string
}

export interface CalendarHeaderCreate {
  name: string
  description?: string | null
  owner_id?: string
}

export interface CalendarHeaderUpdate {
  name?: string
  description?: string | null
  is_active?: boolean
}

export interface CalendarMilestoneRead {
  id: string
  event_id: string
  name: string
  description: string | null
  owner: CalendarOwner | null
  milestone_type: MilestoneType
  display_order: number
  date_mode: DateMode
  specific_date: string | null
  fiscal_year: number | null
  fiscal_week: number | null
  calc_direction: CalcDirection | null
  calc_quantity: number | null
  calc_unit: CalcUnit | null
  calc_from_milestone_id: string | null
  rule_code: string | null
  rule_configuration: Record<string, unknown> | null
  resolved_date: string | null
  created_at: string
  updated_at: string
}

export interface CalendarMilestoneCreate {
  name: string
  description?: string | null
  owner_id?: string | null
  milestone_type: MilestoneType
  date_mode: DateMode
  specific_date?: string | null
  fiscal_year?: number | null
  fiscal_week?: number | null
  calc_direction?: CalcDirection | null
  calc_quantity?: number | null
  calc_unit?: CalcUnit | null
  calc_from_milestone_id?: string | null
  rule_code?: string | null
  rule_configuration?: Record<string, unknown> | null
}

export type CalendarMilestoneUpdate = Partial<CalendarMilestoneCreate>

export interface CalendarEventRead {
  id: string
  calendar_id: string
  name: string
  description: string | null
  owner: CalendarOwner | null
  display_order: number
  milestones: CalendarMilestoneRead[]
  created_at: string
  updated_at: string
}

export interface CalendarEventCreate {
  name: string
  description?: string | null
  owner_id?: string | null
}

export interface CalendarEventUpdate {
  name?: string
  description?: string | null
  owner_id?: string | null
}

export interface ReorderRequest {
  ordered_ids: string[]
}

export interface CalendarDetailResponse extends CalendarHeaderRead {
  events: CalendarEventRead[]
}

export interface PaginatedCalendarsResponse {
  data: CalendarHeaderRead[]
  pagination: PaginationMeta
}

// ─── Plan Browse Types ──────────────────────────────────────────

export interface BrowseDivision {
  id: string
  code: string
  name: string
  plan_count: number
}

export interface PaginatedBrowseDivisionsResponse {
  data: BrowseDivision[]
  pagination: PaginationMeta
}

export interface BrowseDepartment {
  id: string
  code: string
  name: string
  plan_count: number
}

export interface BrowseCalendar {
  calendar_id: string | null
  calendar_name: string
  is_seasonal: boolean
  plan_count: number
}

export interface PaginatedBrowseCalendarsResponse {
  data: BrowseCalendar[]
  pagination: PaginationMeta
}

export interface BrowseCalendarEvent {
  event_id: string
  event_name: string
  plan_count: number
}

export interface PaginatedBrowseCalendarEventsResponse {
  data: BrowseCalendarEvent[]
  pagination: PaginationMeta
}

export interface PaginatedBrowseDepartmentsResponse {
  data: BrowseDepartment[]
  pagination: PaginationMeta
}

export interface CalendarGridColumn {
  name: string
  column_index: number
}

export interface CalendarGridCell {
  column_index: number
  milestone_id: string
  resolved_date: string | null
  date_mode: DateMode
  fiscal_week_label: string | null
  is_editable: boolean
}

export interface CalendarGridRow {
  event_id: string
  event_name: string
  display_order: number
  cells: CalendarGridCell[]
}

export interface CalendarGridResponse {
  columns: CalendarGridColumn[]
  rows: CalendarGridRow[]
}

export interface MilestoneRuleConfigField {
  type: string
  default?: unknown
  description?: string
}

export interface MilestoneRuleRead {
  code: string
  name: string
  description: string
  config_schema: Record<string, MilestoneRuleConfigField>
}

// ─── Plan Calendar Types ─────────────────────────────────────────

export interface PlanMilestoneRead {
  id: string
  plan_id: string
  calendar_milestone_id: string
  milestone_name: string
  event_name: string
  event_id: string
  milestone_type: string
  display_order: number
  target_date: string
  estimated_date: string
  actual_date: string | null
  is_met: boolean
  status_signal: string | null
  estimated_date_override: string | null
  is_auto_calculated: boolean
  notes: string | null
  rule_code: string | null
  created_at: string
  updated_at: string
}

export interface PlanEventGroup {
  event_id: string
  event_name: string
  display_order: number
  milestones: PlanMilestoneRead[]
}

export interface PlanCalendarResponse {
  calendar_id: string
  calendar_name: string
  event_id: string
  event_name: string
  is_seasonal: boolean
  milestones: PlanMilestoneRead[]
}

export interface CalendarEventSummary {
  id: string
  calendar_id: string
  name: string
  description: string | null
  owner_id: string | null
  display_order: number
  milestone_count: number
}
